// ---------------------------------------------------------------------------
// iter84.56.6.2 — FloatingPanel
//
// Lightweight draggable, resizable floating panel rendered via React portal
// so it isn't clipped by ancestor `overflow: hidden`. Persists position +
// size to localStorage under `storageKey`. The user grabs the header bar
// to drag; mouse-up commits the new position. Pure CSS resize-handles in
// the bottom-right corner. No external deps.
// ---------------------------------------------------------------------------
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Move, X } from "lucide-react";

const DEFAULT_PANEL = { x: 24, y: 96, w: 460, h: 540 };

function _loadGeom(storageKey) {
    if (typeof window === "undefined") return DEFAULT_PANEL;
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) return DEFAULT_PANEL;
        const parsed = JSON.parse(raw);
        return {
            x: typeof parsed.x === "number" ? parsed.x : DEFAULT_PANEL.x,
            y: typeof parsed.y === "number" ? parsed.y : DEFAULT_PANEL.y,
            w: typeof parsed.w === "number" ? parsed.w : DEFAULT_PANEL.w,
            h: typeof parsed.h === "number" ? parsed.h : DEFAULT_PANEL.h,
        };
    } catch {
        return DEFAULT_PANEL;
    }
}

function _saveGeom(storageKey, geom) {
    if (typeof window === "undefined") return;
    try {
        const raw = window.localStorage.getItem(storageKey);
        const prev = raw ? JSON.parse(raw) : {};
        window.localStorage.setItem(storageKey, JSON.stringify({ ...prev, ...geom }));
    } catch {
        // ignore
    }
}

export function FloatingPanel({ storageKey, title, badge, badgeClass, onClose, children }) {
    const [geom, setGeom] = useState(() => _loadGeom(storageKey));
    const dragRef = useRef(null);
    const resizeRef = useRef(null);

    // Clamp so the title bar can never escape the viewport.
    useEffect(() => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        setGeom((g) => {
            const x = Math.max(0, Math.min(g.x, vw - 80));
            const y = Math.max(0, Math.min(g.y, vh - 40));
            return { ...g, x, y };
        });
    }, []);

    // Drag handler — listens at the window level after pointerdown on
    // the header. Detaches on pointerup.
    const onHeaderPointerDown = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        const startX = e.clientX;
        const startY = e.clientY;
        const startGeom = { ...geom };
        const onMove = (ev) => {
            const dx = ev.clientX - startX;
            const dy = ev.clientY - startY;
            const next = {
                ...startGeom,
                x: Math.max(0, Math.min(window.innerWidth - 80, startGeom.x + dx)),
                y: Math.max(0, Math.min(window.innerHeight - 40, startGeom.y + dy)),
            };
            setGeom(next);
            dragRef.current = next;
        };
        const onUp = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            if (dragRef.current) {
                _saveGeom(storageKey, dragRef.current);
                dragRef.current = null;
            }
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
    };

    const onResizePointerDown = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startY = e.clientY;
        const startGeom = { ...geom };
        const onMove = (ev) => {
            const dx = ev.clientX - startX;
            const dy = ev.clientY - startY;
            const next = {
                ...startGeom,
                w: Math.max(320, startGeom.w + dx),
                h: Math.max(200, startGeom.h + dy),
            };
            setGeom(next);
            resizeRef.current = next;
        };
        const onUp = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            if (resizeRef.current) {
                _saveGeom(storageKey, resizeRef.current);
                resizeRef.current = null;
            }
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
    };

    if (typeof document === "undefined") return null;

    return createPortal(
        <div
            data-testid="uat-runbook-floating-panel"
            className="fixed z-[60] bg-zinc-950 border-2 border-volt/60 shadow-2xl shadow-volt/20 rounded-sm flex flex-col"
            style={{
                left: geom.x,
                top: geom.y,
                width: geom.w,
                height: geom.h,
            }}
        >
            <div
                data-testid="uat-runbook-floating-panel-header"
                onPointerDown={onHeaderPointerDown}
                className="flex items-center gap-2 px-2.5 py-1.5 bg-gradient-to-r from-volt/15 to-zinc-900 border-b border-volt/40 cursor-move select-none shrink-0"
            >
                <Move className="w-3.5 h-3.5 text-volt shrink-0" />
                <span className="text-[11px] font-bold tracking-widest uppercase text-white truncate flex-1">
                    {title}
                </span>
                {badge && (
                    <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 border rounded-sm shrink-0 ${badgeClass || "border-zinc-700 text-zinc-300"}`}>
                        {badge}
                    </span>
                )}
                <button
                    type="button"
                    onClick={onClose}
                    onPointerDown={(e) => e.stopPropagation()}
                    data-testid="uat-runbook-floating-panel-close"
                    className="text-zinc-400 hover:text-rose-400 transition-colors shrink-0"
                    title="Dock back into the card"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
                {children}
            </div>
            <div
                onPointerDown={onResizePointerDown}
                data-testid="uat-runbook-floating-panel-resize"
                className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize text-zinc-500 hover:text-volt"
                title="Drag to resize"
                style={{
                    background: "linear-gradient(135deg, transparent 50%, currentColor 50%, currentColor 60%, transparent 60%, transparent 70%, currentColor 70%, currentColor 80%, transparent 80%)",
                }}
            />
        </div>,
        document.body,
    );
}
